import { ReactNode, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import authContext from "jwt_context&axios/authContext";
import { FinallyMigrationBanner } from "components/Auth/FinallyMigrationBanner";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { Inkle } from "components/icons/Logos/Inkle";
import { InvitationContext } from "contexts/InvitationContext";
import { InvitedSignupContent } from "v2/components/onboarding/InvitedSignupContent";
import { NeedHelp } from "v2/components/onboarding/NeedHelp/NeedHelp";
import {
  clearOnboardingDataStorage,
  OnboardingDataProvider,
  useOnboardingData,
  useOnboardingState,
} from "v2/components/onboarding/OnboardingDataContext";
import { OnboardingStepper } from "v2/components/onboarding/OnboardingStepper";
import { ConfirmEntityStep } from "v2/components/onboarding/steps/ConfirmEntityStep/ConfirmEntityStep";
import { buildConfirmEntitySummary } from "v2/components/onboarding/steps/ConfirmEntityStep/confirmEntitySummary";
import { DomainRecognize } from "v2/components/onboarding/steps/ConfirmEntityStep/DomainRecognize";
import {
  buildEntityTypeSummary,
  EntityTypeStep,
} from "v2/components/onboarding/steps/EntityTypeStep/EntityTypeStep";
import {
  buildPersonalDetailsSummary,
  PersonalDetailsStep,
} from "v2/components/onboarding/steps/PersonalDetailsStep/PersonalDetailsStep";
import {
  buildPhoneSummary,
  PhoneStep,
} from "v2/components/onboarding/steps/PhoneStep/PhoneStep";
import {
  buildReviewEntitySummary,
  getReviewMandatoryError,
  ReviewEntityStep,
} from "v2/components/onboarding/steps/ReviewEntityStep/ReviewEntityStep";
import {
  useApplyOnboardingCouponMutation,
  useGetGroupReviewDetailsQuery,
  useTriggerGroupVerificationMutation,
} from "v2/store/onboardingEntityApi";
import { flushEntityReviewSaves } from "v2/utils/entityReviewAutosaveQueue";
import { readError } from "v2/utils/readError";
import {
  useApplyInviteCouponMutation,
  useGetCountriesDataQuery,
} from "store/apis/onboarding";
import {
  INVITED_STEP_CONFIG,
  ONBOARDING_STEP,
  STEP_STATUS,
  StepId,
} from "v2/components/onboarding/types";
import { isOnServiceTeam } from "utils/isOnServiceTeam";
import { joinLegalName } from "v2/utils/joinLegalName";
import { isAuthTokenPresent } from "v2/utils/isAuthTokenPresent";
import { useSignupResume } from "v2/utils/useSignupResume";
import { useSyncOnboardingState } from "v2/utils/useSyncOnboardingState";
import { ONBOARDING_STEP_KEY } from "v2/constants/onboardingStepKey";
import {
  useGetOnboardingStateQuery,
  useSaveOnboardingStateMutation,
} from "v2/store/onboardingStateApi";
import { useStoreReferralCode } from "v2/utils/useStoreReferralCode";
import {
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
} from "store/apis/profile";
import { BareInput } from "components/DesignSystem/Input/Input";
import { Button } from "components/DesignSystem/Button/Button";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { ConditionalLink } from "components/conditionalLink";
import { RightArrow } from "components/icons/RightArrow";
import { TrashSimple } from "components/icons/TrashSimple";
import { useToast } from "hooks/useToast";
import { usePageTitle } from "hooks/usePageTitle";

const COUPON_CODE_STORAGE_KEY = "couponCode";

const NO_DONE_STEPS: StepId[] = [];
const INVITED_STEP_IDS: StepId[] = INVITED_STEP_CONFIG.map((step) => step.id);

const SignupContent = () => {
  const history = useHistory();
  const onboarding = useOnboardingState();
  const { data } = useOnboardingData();
  const [getProfile] = useLazyGetUserProfileQuery();
  const [showReviewErrors, setShowReviewErrors] = useState(false);

  useSyncOnboardingState({
    doneSteps: onboarding.doneStepIds,
    formationTypeId: data.entityTypeId,
  });

  const isStep5Done =
    onboarding.getStatus(ONBOARDING_STEP.REVIEW_ENTITY) === "done";
  const { data: reviewDetails } = useGetGroupReviewDetailsQuery(
    { groupId: data.groupId },
    { skip: !data.groupId || !isStep5Done }
  );

  useEffect(() => {
    const isReady =
      reviewDetails &&
      reviewDetails.length > 0 &&
      reviewDetails.every((entity) => !entity.verificationPending);
    if (!isStep5Done || !isReady) return;
    onboarding.setSummary(
      ONBOARDING_STEP.REVIEW_ENTITY,
      buildReviewEntitySummary(data.companyName, reviewDetails)
    );
  }, [reviewDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { setSummary, getStatus } = onboarding;
    const isDone = (id: number) => getStatus(id as StepId) === "done";

    if (isDone(ONBOARDING_STEP.PERSONAL_DETAILS) && data.firstName) {
      setSummary(
        ONBOARDING_STEP.PERSONAL_DETAILS,
        buildPersonalDetailsSummary({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        })
      );
    }
    if (isDone(ONBOARDING_STEP.PHONE) && data.mobile) {
      setSummary(ONBOARDING_STEP.PHONE, buildPhoneSummary(data.mobile));
    }
    if (isDone(ONBOARDING_STEP.CONFIRM_ENTITY) && data.companyName) {
      setSummary(
        ONBOARDING_STEP.CONFIRM_ENTITY,
        buildConfirmEntitySummary(
          data.companyName,
          joinLegalName(data.entityName, data.entitySuffix)
        )
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data.entityTypeLabel) return;

    onboarding.setSummary(
      ONBOARDING_STEP.ENTITY_TYPE,
      buildEntityTypeSummary(data.entityTypeLabel)
    );
  }, [data.entityTypeLabel, onboarding.setSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhoneComplete = async (summary: ReactNode) => {
    onboarding.completeStep(ONBOARDING_STEP.PHONE, summary);

    const profile = await getProfile()
      .unwrap()
      .catch(() => null);

    if (!profile) return;

    const belongsElsewhere =
      profile.is_group_created ||
      (isOnServiceTeam(profile) && !profile.is_restricted);

    if (belongsElsewhere) history.replace("/");
  };

  const renderStepBody = (id: StepId): ReactNode => {
    const onComplete = (summary: ReactNode) =>
      onboarding.completeStep(id, summary);

    if (id === ONBOARDING_STEP.ENTITY_TYPE) {
      return <EntityTypeStep onComplete={onComplete} />;
    }
    if (id === ONBOARDING_STEP.PERSONAL_DETAILS) {
      return <PersonalDetailsStep onComplete={onComplete} />;
    }
    if (id === ONBOARDING_STEP.PHONE) {
      return (
        <PhoneStep
          onComplete={(summary) => void handlePhoneComplete(summary)}
        />
      );
    }
    if (id === ONBOARDING_STEP.CONFIRM_ENTITY) {
      const handleError = () =>
        onboarding.goToStep(ONBOARDING_STEP.CONFIRM_ENTITY);
      const isConfirmed =
        Boolean(data.groupId) ||
        data.entities.some((entity) => entity.entityId);
      if (isConfirmed) {
        return (
          <ConfirmEntityStep onComplete={onComplete} onError={handleError} />
        );
      }
      return <DomainRecognize onComplete={onComplete} onError={handleError} />;
    }
    if (id === ONBOARDING_STEP.REVIEW_ENTITY) {
      return (
        <ReviewEntityStep
          onComplete={onComplete}
          showErrors={showReviewErrors}
        />
      );
    }
    return null;
  };

  const showDoneFooter =
    onboarding.currentStep === ONBOARDING_STEP.REVIEW_ENTITY;

  return (
    <>
      <OnboardingStepper
        onboarding={onboarding}
        renderStepBody={renderStepBody}
      />
      {showDoneFooter && (
        <SignupDoneFooter onValidationFail={() => setShowReviewErrors(true)} />
      )}
    </>
  );
};

type SignupDoneFooterProps = {
  onValidationFail: () => void;
};

const SignupDoneFooter = ({ onValidationFail }: SignupDoneFooterProps) => {
  const history = useHistory();
  const { data } = useOnboardingData();
  const { alertToast } = useToast();

  const { data: profile } = useGetUserProfileQuery();
  const groupId = data.groupId || profile?.company_group || "";

  const { data: reviewData } = useGetGroupReviewDetailsQuery(
    { groupId },
    { skip: !groupId }
  );

  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState(
    () => localStorage.getItem(COUPON_CODE_STORAGE_KEY) || ""
  );
  const [appliedCode, setAppliedCode] = useState("");
  const [isDone, setIsDone] = useState(false);

  const [verifyInviteCoupon, { isLoading: isVerifying }] =
    useApplyInviteCouponMutation();
  const [applyOnboardingCoupon] = useApplyOnboardingCouponMutation();
  const [triggerGroupVerification] = useTriggerGroupVerificationMutation();

  const { data: recordedState } = useGetOnboardingStateQuery();
  const [saveOnboardingState] = useSaveOnboardingStateMutation();

  const recordReviewSubmitted = async () => {
    const recordedKeys = recordedState?.completedSteps ?? [];
    if (recordedKeys.includes(ONBOARDING_STEP_KEY.REVIEW_ENTITY)) return;

    await saveOnboardingState({
      completedSteps: [...recordedKeys, ONBOARDING_STEP_KEY.REVIEW_ENTITY],
      formationTypeId: recordedState?.formationTypeId || "",
    }).unwrap();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("couponCode") || params.get("couponcode");
    if (code) {
      localStorage.setItem(COUPON_CODE_STORAGE_KEY, code);
      setCouponCode(code);
      setShowCouponInput(true);
    }
  }, []);

  const handleVerify = async () => {
    if (!couponCode.trim()) return;
    try {
      const result = await verifyInviteCoupon({
        uuid: data.profileId,
        couponCode: couponCode.trim(),
      }).unwrap();
      if (result.is_valid_coupon) {
        setAppliedCode(couponCode.trim());
      } else {
        alertToast({ message: "Invalid coupon code. Please try again." });
      }
    } catch (error) {
      const message =
        (error as { data?: { error?: { message?: string } } })?.data?.error
          ?.message || "Failed to verify coupon.";
      alertToast({ message });
    }
  };

  const handleRemove = () => {
    setAppliedCode("");
    setCouponCode("");
    localStorage.removeItem(COUPON_CODE_STORAGE_KEY);
  };

  const handleCouponKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !appliedCode) {
      void handleVerify();
    }
  };

  const handleDone = async () => {
    const mandatoryError = reviewData
      ? getReviewMandatoryError(reviewData)
      : null;
    if (mandatoryError) {
      onValidationFail();
      alertToast({ message: mandatoryError });
      return;
    }

    setIsDone(true);
    try {
      await flushEntityReviewSaves();

      if (appliedCode) {
        await applyOnboardingCoupon({
          groupId,
          couponCode: appliedCode,
        }).unwrap();
      }

      await recordReviewSubmitted();
      await triggerGroupVerification({ groupId }).unwrap();

      clearOnboardingDataStorage();
      history.replace("/");
    } catch (error) {
      const message =
        (error as { data?: { error?: { message?: string } } })?.data?.error
          ?.message ?? readError(error);
      alertToast({ message });
      setIsDone(false);
    }
  };

  return (
    <div className="t-mt-5 t-flex t-flex-col t-gap-5 t-pl-14">
      <div className="t-flex t-items-start t-gap-4">
        <span className="t-flex t-h-8 t-shrink-0 t-items-center t-text-body-sm t-font-medium t-text-text-60">
          Have a coupon code?
        </span>

        {!showCouponInput && !appliedCode && (
          <Button
            size="small"
            customType="link"
            className="t-self-center"
            onClick={() => setShowCouponInput(true)}
          >
            Apply
          </Button>
        )}

        {showCouponInput && (
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-flex t-items-center t-gap-2">
              <div className="t-w-[200px]">
                <BareInput
                  customSize="small"
                  placeholder="Enter code"
                  value={couponCode}
                  disabled={Boolean(appliedCode)}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyDown={handleCouponKeyDown}
                  rightComponent={
                    appliedCode ? (
                      <CheckCircleFilled className="t-text-green-80" />
                    ) : undefined
                  }
                />
              </div>

              {appliedCode ? (
                <Button
                  customType="ghost_icon"
                  type="button"
                  size="small"
                  onClick={handleRemove}
                  className="t-flex t-cursor-pointer t-items-center t-border-0 t-bg-transparent t-p-0 t-text-text-30 hover:t-text-red"
                >
                  <TrashSimple />
                </Button>
              ) : (
                <Button
                  customType="secondary"
                  size="small"
                  isLoading={isVerifying}
                  onClick={handleVerify}
                  disabled={!couponCode.trim()}
                >
                  Apply code
                </Button>
              )}
            </div>

            {Boolean(appliedCode) && (
              <span className="t-text-body-sm-italic t-italic t-text-green-80">
                Applied successfully
              </span>
            )}
          </div>
        )}
      </div>

      <div className="t-h-px t-bg-neutral-0" />

      <div>
        <Button
          customType="primary"
          rightIcon={<RightArrow />}
          isLoading={isDone}
          onClick={handleDone}
        >
          Done, take me in
        </Button>
      </div>
    </div>
  );
};

const SignupResolver = () => {
  const history = useHistory();
  const {
    isLoading,
    isSignupComplete,
    initialStep,
    initialDoneSteps,
    initialData,
  } = useSignupResume();

  useEffect(() => {
    if (isLoading || !isSignupComplete) return;

    clearOnboardingDataStorage();
    history.replace("/");
  }, [isLoading, isSignupComplete, history]);

  if (isLoading || isSignupComplete) {
    return (
      <div className="t-flex t-h-screen t-w-full t-items-center t-justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <OnboardingDataProvider
      initialStep={initialStep}
      initialDoneSteps={initialDoneSteps}
      initialData={initialData}
    >
      <SignupLayout>
        <SignupContent />
      </SignupLayout>
    </OnboardingDataProvider>
  );
};

const InvitedResolver = () => {
  const history = useHistory();
  const hasToken = isAuthTokenPresent();

  const { data: profile, isLoading } = useGetUserProfileQuery(undefined, {
    skip: !hasToken,
  });

  const isAlreadyVerified = Boolean(profile?.is_mobile_verified);

  useEffect(() => {
    if (!isAlreadyVerified) return;

    history.replace("/");
  }, [isAlreadyVerified, history]);

  if ((hasToken && isLoading) || isAlreadyVerified) {
    return (
      <div className="t-flex t-h-screen t-w-full t-items-center t-justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <OnboardingDataProvider
      initialStep={ONBOARDING_STEP.PERSONAL_DETAILS}
      initialDoneSteps={NO_DONE_STEPS}
      steps={INVITED_STEP_IDS}
    >
      <SignupLayout>
        <InvitedSignupContent />
      </SignupLayout>
    </OnboardingDataProvider>
  );
};

const SignupTopBar = () => {
  const { getStatus } = useOnboardingState();
  const { logoutUser } = useContext(authContext);

  const isPersonalDetailsDone =
    getStatus(ONBOARDING_STEP.PERSONAL_DETAILS) === STEP_STATUS.DONE;

  return (
    <div className="t-sticky t-top-0 t-z-10 t-flex t-w-full t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-white t-px-8 t-py-4">
      <Inkle />
      {isPersonalDetailsDone ? (
        <Button customType="link" onClick={logoutUser}>
          Log out
        </Button>
      ) : (
        <div className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-30">
          Already have an account?
          <ConditionalLink to="/signin">Sign In</ConditionalLink>
        </div>
      )}
    </div>
  );
};

const SignupLayout = ({ children }: { children: ReactNode }) => {
  return (
    <div className="t-flex t-h-screen t-w-full t-flex-col t-overflow-hidden">
      <SignupTopBar />
      <FinallyMigrationBanner />
      <NeedHelp />
      <div className="t-flex-1 t-overflow-y-auto t-bg-neutral-66">
        <div className="t-mx-auto t-w-full t-max-w-[860px] t-px-8 t-pb-40 t-pt-12 sm:t-px-4 sm:t-pt-6">
          <div className="t-mb-12 sm:t-mb-6 sm:t-px-3">
            <h1 className="t-m-0 t-text-title-h1 t-font-normal t-text-text-60">
              Let&apos;s get your account ready
            </h1>
            <p className="t-mt-1.5 t-text-body t-text-text-30">
              A few quick steps and you&apos;re in. All of this can be updated
              from settings later.
            </p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export const Signup = () => {
  const isInvited = useContext(InvitationContext);

  usePageTitle("Sign Up");

  useStoreReferralCode();

  useGetCountriesDataQuery();

  if (isInvited) return <InvitedResolver />;

  return <SignupResolver />;
};
